import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { SlidersHorizontal, X } from "lucide-react";
import { api, PROPERTY_TYPES, CITIES } from "@/lib/loop";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import PropertyCard from "@/components/PropertyCard";
import Footer from "@/components/Footer";

const SORTS = [
  { v: "relevance", l: "Relevance" },
  { v: "newest", l: "Newest verified" },
  { v: "price_low", l: "Price: low to high" },
  { v: "price_high", l: "Price: high to low" },
  { v: "area", l: "Area" },
  { v: "updated", l: "Recently updated" },
];

const VERIF = [
  { v: "verified", l: "Verified" },
  { v: "documents_pending", l: "Documents Pending" },
  { v: "unverified", l: "Unverified" },
];

function FilterPanel({ f, set, reset }) {
  const upd = (k, v) => set({ ...f, [k]: v === "any" ? "" : v });
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-white uppercase tracking-wider">Filters</h3>
        <button data-testid="reset-filters" onClick={reset} className="text-xs text-[#C5A059] hover:underline">Reset</button>
      </div>

      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Listing</Label>
        <div className="mt-2 flex gap-2">
          {["", "buy", "rent"].map((l) => (
            <button key={l || "all"} data-testid={`filter-listing-${l || "all"}`} onClick={() => upd("listing_type", l || "any")}
              className={`flex-1 py-1.5 rounded-lg text-xs font-medium capitalize border transition-colors
                ${f.listing_type === l ? "bg-[#C5A059] text-[#070C1E] border-[#C5A059]" : "text-slate-300 border-white/15"}`}>
              {l || "All"}
            </button>
          ))}
        </div>
      </div>

      {[["city", "City", CITIES.map((c) => ({ value: c, label: c }))],
        ["property_type", "Property type", PROPERTY_TYPES],
        ["verification_status", "Verification status", VERIF.map((v) => ({ value: v.v, label: v.l }))],
        ["facing", "Facing", ["East", "West", "North", "South", "North-East", "South-East"].map((v) => ({ value: v, label: v }))],
        ["listed_by", "Listed by", [{ value: "owner", label: "Owner listed" }, { value: "agent", label: "Agent listed" }]],
        ["new_or_resale", "New / Resale", [{ value: "new", label: "New" }, { value: "resale", label: "Resale" }]],
        ["rera", "RERA", [{ value: "registered", label: "Registered" }, { value: "not_applicable", label: "Not applicable" }]],
      ].map(([key, label, opts]) => (
        <div key={key}>
          <Label className="text-xs uppercase tracking-wider text-slate-500">{label}</Label>
          <Select value={f[key] || "any"} onValueChange={(v) => upd(key, v)}>
            <SelectTrigger data-testid={`filter-${key}`} className="mt-1.5 bg-[#131B35] border-white/10 text-white">
              <SelectValue placeholder="Any" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="any">Any</SelectItem>
              {opts.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      ))}

      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Min ₹</Label>
          <Input data-testid="filter-min-price" type="number" value={f.min_price}
            onChange={(e) => set({ ...f, min_price: e.target.value })}
            className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="0" />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider text-slate-500">Max ₹</Label>
          <Input data-testid="filter-max-price" type="number" value={f.max_price}
            onChange={(e) => set({ ...f, max_price: e.target.value })}
            className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="Any" />
        </div>
      </div>
      <div>
        <Label className="text-xs uppercase tracking-wider text-slate-500">Bedrooms</Label>
        <Input data-testid="filter-bedrooms" type="number" value={f.bedrooms}
          onChange={(e) => set({ ...f, bedrooms: e.target.value })}
          className="mt-1.5 bg-[#131B35] border-white/10 text-white" placeholder="Any" />
      </div>
    </div>
  );
}

export default function Search() {
  const [sp, setSp] = useSearchParams();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("relevance");
  const [f, setF] = useState({
    q: sp.get("q") || "", city: sp.get("city") || "", property_type: sp.get("property_type") || "",
    listing_type: sp.get("listing_type") || "", verification_status: sp.get("verification_status") || "",
    facing: "", listed_by: "", new_or_resale: "", rera: "", min_price: "", max_price: "", bedrooms: "",
  });

  const fetchResults = () => {
    setLoading(true);
    const params = { sort };
    Object.entries(f).forEach(([k, v]) => { if (v !== "" && v != null) params[k] = v; });
    api.get("/properties", { params }).then((r) => setResults(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { fetchResults(); /* eslint-disable-next-line */ }, [f, sort]);

  const reset = () => setF({ q: "", city: "", property_type: "", listing_type: "", verification_status: "",
    facing: "", listed_by: "", new_or_resale: "", rera: "", min_price: "", max_price: "", bedrooms: "" });

  return (
    <div className="min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 lg:px-12 py-8">
        {/* top bar */}
        <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between mb-6">
          <Input data-testid="search-top-input" value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })}
            placeholder="Search locality, project or Property ID" className="sm:max-w-md bg-[#131B35] border-white/10 text-white" />
          <div className="flex items-center gap-3">
            <Sheet>
              <SheetTrigger asChild>
                <Button data-testid="mobile-filter-btn" variant="outline" className="lg:hidden border-white/15 text-slate-200">
                  <SlidersHorizontal className="w-4 h-4 mr-2" /> Filters
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="bg-[#0B132B] border-white/10 overflow-y-auto w-[85vw]">
                <div className="pt-6"><FilterPanel f={f} set={setF} reset={reset} /></div>
              </SheetContent>
            </Sheet>
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger data-testid="sort-select" className="w-48 bg-[#131B35] border-white/10 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SORTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex gap-8">
          <aside className="hidden lg:block w-72 shrink-0">
            <div className="sticky top-24 bg-[#0B132B] border border-white/10 rounded-2xl p-6">
              <FilterPanel f={f} set={setF} reset={reset} />
            </div>
          </aside>

          <div className="flex-1">
            <p data-testid="results-count" className="text-sm text-slate-400 mb-4">
              {loading ? "Searching…" : `${results.length} properties found`}
            </p>
            {!loading && results.length === 0 && (
              <div className="text-center py-24 text-slate-500">No properties match your filters.</div>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6">
              {results.map((p, i) => <PropertyCard key={p.id} p={p} index={i} testid={`search-card-${i}`} />)}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
